import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import { httpAPI_RET018 } from "./api/request";
import { fakeTableList, schema, stepper } from "./parts";
import "./_style.scss";
import { Box, Typography } from "@mui/material";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import { IAPI_RET018_Request } from "../../type/apiType";
import { API_RET018_BuildReqParams } from "../../utils/buildRequestParams";
import { FormKbn } from "../../assets/data/Constants";
import _ from "lodash";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";

/**
 * SCRR411121 退職手当金受領者の変更手続き（請求権者の証明書類の添付）
 * @return SCRR411121
 */
const SCRR411121 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const [stateTableList, setStateTableList] = useState<any>([]);
  const userType = state.authInfo?.userType;
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const requestContrcatorNo = isUserTypeKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const rotationData = businessState.job_40 ?? {};
  const [ruleSchema, setRuleSchema] = useState<any>(schema);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [openSuccess, setOpenSuccess] = useState(false);

  // 必要書類に関する文言を取得
  const getTextMailer = (
    selectedRelationShip: string | undefined,
    livelihoodFromRetireeFlg: string | undefined
  ) => {
    if (selectedRelationShip === "1") {
      return (
        <Typography>
          ２．配偶者の「戸籍謄本」（但し、職員の死亡後に別戸籍となっている場合は、配偶者の「戸籍謄本」のほか職員の死亡を証する「戸籍謄本」又は「除籍謄本」）
        </Typography>
      );
    } else if (selectedRelationShip === "2") {
      return (
        <Typography>
          <span className="textDocumentBr">
            ２．職員の死亡及び戸籍上の配偶者がいないことを証する「戸籍謄本」又は「除籍謄本」
          </span>
          <span className="textDocumentBr">
            ３．内縁の妻又は夫の「戸籍謄本」
          </span>
        </Typography>
      );
    } else {
      if (livelihoodFromRetireeFlg === "1") {
        return (
          <Typography>
            <span className="textDocumentBr">
              ３．職員の死亡を証する「戸籍謄本」又は「除籍謄本」
            </span>
            <span className="textDocumentBr">
              ４．職員と請求者（受給権者）の身分関係及び先順位の遺族がいないことを証する「戸籍謄本」（「除籍謄本」、「改製原戸籍謄本」を含む）
            </span>
            <span className="textDocumentBr">
              ５．上記３又は４の戸籍謄本より請求者（受給権者）が除籍されている場合は、請求者（受給権者）の現在の「戸籍抄本」又は「戸籍謄本」
            </span>
          </Typography>
        );
      } else if (livelihoodFromRetireeFlg === "0") {
        return (
          <Typography>
            <span className="textDocumentBr">
              ３．「認証文つき法定相続情報一覧図の写し」（注）３は以下の４～６に代替えできます。
            </span>
            <span className="textDocumentBr">
              ４．職員の死亡を証する「戸籍謄本」又は「除籍謄本」
            </span>
            <span className="textDocumentBr">
              ５．職員と請求者（受給権者）の身分関係及び先順位の遺族がいないことを証する「戸籍謄本」（「除籍謄本」、「改製原戸籍謄本」を含む）
            </span>
            <span className="textDocumentBr">
              　※職員の出生から死亡まで連続した期間の「戸籍謄本」（「除籍謄本」、「改製原戸籍謄本」を含む）等
            </span>
            <span className="textDocumentBr">
              ６．上記４又は５の戸籍謄本より請求者（受給権者）が除籍されている場合は、請求者（受給権者）の現在の「戸籍抄本」又は「戸籍謄本」
            </span>
          </Typography>
        );
      } else {
        return <Typography></Typography>;
      }
    }
  };

  const handleInit = async () => {
    let resArr = stateFakeTableList.current;

    const fileKeyClassList = [
      { key: "deathCertificate", class: "14" },
      { key: "livelihoodSupportRelatives", class: "10" },
      { key: "livelihoodNoSupportRelatives", class: "10" },
      { key: "livelihoodSupportWithoutFormalMarriage", class: "10" },
      { key: "certificateOfResidence", class: "21" },
      { key: "lawfulPartner", class: "22" },
    ];
    fileKeyClassList.forEach((fileKeyClass) => {
      (rotationData.fileList ?? []).forEach((file) => {
        const fileInfo = file;
        let fileObj = resArr.find(
          (item) =>
            item.keyword === fileKeyClass.key &&
            fileInfo?.formClass === fileKeyClass.class
        )!;
        fileObj &&
          (fileObj.setDefaultValue = [
            {
              ...file,
              formName: fileInfo.formName,
              fileBase64: fileInfo.file,
            },
          ]);
      });
    });

    // 「死亡診断書」又は「死体検案書」（写）
    let deathCertificate = resArr.find(
      (item) => item.keyword === "deathCertificate"
    )!;
    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいることを証する「生計維持に関する調査書」（様式１１号）
    let livelihoodSupportRelatives = resArr.find(
      (item) => item.keyword === "livelihoodSupportRelatives"
    )!;
    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいないことを証する「生計維持に関する調査書」（様式１１号）
    let livelihoodNoSupportRelatives = resArr.find(
      (item) => item.keyword === "livelihoodNoSupportRelatives"
    )!;
    // 内縁関係についての「生計維持に関する調査書」（様式１１号）
    let livelihoodSupportWithoutFormalMarriage = resArr.find(
      (item) => item.keyword === "livelihoodSupportWithoutFormalMarriage"
    )!;
    // 職員の死亡当時の世帯員の状況を明らかにできる「住民票の謄本」
    let certificateOfResidence = resArr.find(
      (item) => item.keyword === "certificateOfResidence"
    )!;
    // その他、事実上の配偶者であることを証明する書類
    let lawfulPartner = resArr.find(
      (item) => item.keyword === "lawfulPartner"
    )!;

    // ファイルアップロード部品Id付与
    deathCertificate.id = "fileUploadId1";
    livelihoodSupportRelatives.id = "fileUploadId2";
    livelihoodNoSupportRelatives.id = "fileUploadId3";
    livelihoodSupportWithoutFormalMarriage.id = "fileUploadId4";
    certificateOfResidence.id = "fileUploadId5";
    lawfulPartner.id = "fileUploadId6";

    if (rotationData.demanderRelationship === "1") {
      deathCertificate.showLine = true;
      livelihoodSupportRelatives.showLine = false;
      livelihoodNoSupportRelatives.showLine = false;
      livelihoodSupportWithoutFormalMarriage.showLine = false;
      certificateOfResidence.showLine = false;
      lawfulPartner.showLine = false;
    } else if (rotationData.demanderRelationship === "2") {
      deathCertificate.showLine = true;
      livelihoodSupportRelatives.showLine = false;
      livelihoodNoSupportRelatives.showLine = false;
      livelihoodSupportWithoutFormalMarriage.showLine = true;
      certificateOfResidence.showLine = true;
      lawfulPartner.showLine = true;
    } else {
      if (rotationData.livelihoodFromRetireeFlg === "1") {
        deathCertificate.showLine = true;
        livelihoodSupportRelatives.showLine = true;
        livelihoodNoSupportRelatives.showLine = false;
        livelihoodSupportWithoutFormalMarriage.showLine = false;
        certificateOfResidence.showLine = false;
        lawfulPartner.showLine = false;
      } else {
        deathCertificate.showLine = true;
        livelihoodSupportRelatives.showLine = false;
        livelihoodNoSupportRelatives.showLine = true;
        livelihoodSupportWithoutFormalMarriage.showLine = false;
        certificateOfResidence.showLine = false;
        lawfulPartner.showLine = false;
      }
    }

    // 郵送対象の一覧
    let textMailing = resArr.find((item) => item.keyword === "textMailing")!;
    textMailing.lineTitle = getTextMailer(
      rotationData.demanderRelationship,
      rotationData.livelihoodFromRetireeFlg
    );

    // 「死亡診断書」又は「死体検案書」（写）バリデーション
    const validatePartner = (data: any) => {
      if (!data || data?.length === 0) {
        return TkPropertyMessageGet("TKMSG_SCR0043E");
      } else {
        return;
      }
    };

    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいることを証する「生計維持に関する調査書」（様式１１号）バリデーション
    const validateLivelihoodSupportRelatives = (data: any) => {
      if (
        (!data || data?.length === 0) &&
        rotationData.demanderRelationship &&
        rotationData.demanderRelationship !== "1" &&
        rotationData.demanderRelationship !== "2" &&
        rotationData.livelihoodFromRetireeFlg === "1"
      ) {
        return TkPropertyMessageGet("TKMSG_SCR0043E");
      } else {
        return;
      }
    };

    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいないことを証する「生計維持に関する調査書」（様式１１号）バリデーション
    const validateLivelihoodNoSupportRelatives = (data: any) => {
      if (
        (!data || data?.length === 0) &&
        rotationData.demanderRelationship &&
        rotationData.demanderRelationship !== "1" &&
        rotationData.demanderRelationship !== "2" &&
        rotationData.livelihoodFromRetireeFlg === "0"
      ) {
        return TkPropertyMessageGet("TKMSG_SCR0043E");
      } else {
        return;
      }
    };

    // 下記書類のバリデーション
    // 内縁関係についての「生計維持に関する調査書」（様式１１号）
    // 職員の死亡当時の世帯員の状況を明らかにできる「住民票の謄本」
    // その他、事実上の配偶者であることを証明する書類
    const validateOther = (data: any) => {
      if (
        (!data || data?.length === 0) &&
        rotationData.demanderRelationship === "2"
      ) {
        return TkPropertyMessageGet("TKMSG_SCR0043E");
      } else {
        return;
      }
    };

    setRuleSchema({
      deathCertificate: {
        validate: validatePartner,
      },
      livelihoodSupportRelatives: {
        validate: validateLivelihoodSupportRelatives,
      },
      livelihoodNoSupportRelatives: {
        validate: validateLivelihoodNoSupportRelatives,
      },
      livelihoodSupportWithoutFormalMarriage: {
        validate: validateOther,
      },
      certificateOfResidence: {
        validate: validateOther,
      },
      lawfulPartner: {
        validate: validateOther,
      },
    });

    setStateTableList([...resArr]);
    setBusinessState({
      ...businessState,
    });
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeHttpObj = (fileList: any): IAPI_RET018_Request => {
    const reqObj: IAPI_RET018_Request = {
      ...rotationData,
      tempSaveStep: 3,
      contractorNo: requestContrcatorNo,
      fileList,
      screenId: "SCRR411121",
    };

    return API_RET018_BuildReqParams(reqObj);
  };

  const onCheckAndSubmit = async (data: any, index?: number) => {
    const fileList =
      rotationData.fileList?.filter(
        (item) =>
          !(
            item.formKbn === "6" &&
            (item.formClass === "10" ||
              item.formClass === "14" ||
              item.formClass === "21" ||
              item.formClass === "22")
          )
      ) ?? [];

    const fileData = [
      { key: "deathCertificate", formClass: "14" },
      { key: "livelihoodSupportRelatives", formClass: "10" },
      { key: "livelihoodNoSupportRelatives", formClass: "10" },
      { key: "livelihoodSupportWithoutFormalMarriage", formClass: "10" },
      { key: "certificateOfResidence", formClass: "21" },
      { key: "lawfulPartner", formClass: "22" },
    ];

    fileData.forEach((file) => {
      if (data?.[file.key]) {
        let oldFile = rotationData?.fileList?.find(
          (item) => item.formClass === file.formClass
        );
        fileList.push({
          facilityNo: oldFile?.facilityNo,
          staffNo: rotationData.staffNo,
          formKbn: FormKbn["退職書類"],
          formClass: file.formClass,
          formName: data[file.key][0].formName,
          file: data[file.key][0].fileBase64,
          issueDocumentNo: oldFile?.issueDocumentNo,
          issueDate: oldFile?.issueDate,
        });
      }
    });

    fileList.forEach((item, index) => {
      item.serialNo = index + 1;
    });

    if (index === 0) {
      try {
        // 退職手当金請求更新API呼び出し
        const res = await httpAPI_RET018({
          ...makeHttpObj(fileList),
        });

        if (res.resultCd === "I0000") {
          setBusinessState({
            ...businessState,
            job_40: {
              ...rotationData,
              tempSaveStep: 3,
              applyId: res.applyId,
              fileList,
            },
          });
          setState({
            ...state,
            errorMsgId: "",
          });
          setOpenSuccess(true);
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      setBusinessState({
        ...businessState,
        job_40: {
          ...rotationData,
          fileList,
        },
      });
      navigate("/SCRR410022");
    }
  };

  return (
    <Box className="SCRR411121" data-testid="SCRR411121PageForTest">
      <TkStepper
        steps={stepper}
        activeStep={2}
        savaStep={rotationData.tempSaveStep}
      />
      <TkInfoManual children={labelids.labelid.LBLR411121_01} rule={"\\n"} />
      {isUserTypeKiko && (
        <TkContractorAndNumberLine
          name="退職者名"
          no={rotationData.retirementStaffName!}
        />
      )}
      <TkFakeTable
        list={stateTableList}
        ruleSchema={ruleSchema}
        onCheckAndSubmit={(data, index) => {
          onCheckAndSubmit(data, index);
        }}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
      />

      <TkMessageDialog
        open={openSuccess}
        onOky={() => setOpenSuccess(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411121;
